import React, { useEffect, useState, useRef, useCallback } from 'react';
import { driveAPI } from '../services/api';
import toast from 'react-hot-toast';
import { useConfirm } from './ConfirmModal';

// ── Mime type → icon + color ─────────────────────────────────────────────────
const FILE_META = {
  'application/vnd.google-apps.document':     { icon: '📄', label: 'Google Doc',   color: '#4285f4' },
  'application/vnd.google-apps.spreadsheet':  { icon: '📊', label: 'Sheet',        color: '#34a853' },
  'application/vnd.google-apps.presentation': { icon: '📽', label: 'Slides',       color: '#fbbc04' },
  'application/vnd.google-apps.folder':       { icon: '📁', label: 'Folder',       color: '#f59e0b' },
  'application/pdf':                          { icon: '📕', label: 'PDF',          color: '#ef4444' },
  'image/png':  { icon: '🖼', label: 'Image', color: 'var(--pm-secondary)' },
  'image/jpeg': { icon: '🖼', label: 'Image', color: 'var(--pm-secondary)' },
  'image/gif':  { icon: '🖼', label: 'Image', color: 'var(--pm-secondary)' },
  'image/webp': { icon: '🖼', label: 'Image', color: 'var(--pm-secondary)' },
  'video/mp4':  { icon: '🎬', label: 'Video', color: '#ec4899' },
  'text/plain': { icon: '📝', label: 'Text',  color: '#64748b' },
  'application/zip': { icon: '🗜', label: 'ZIP', color: '#78716c' },
};
const fileMeta = (mime) => FILE_META[mime] || { icon: '📎', label: 'File', color: '#64748b' };

const fmtSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';

const isTokenExpiredMsg = (e) => {
  const msg = (e?.response?.data?.message || e?.message || '').toLowerCase();
  return msg.includes('token expired') || msg.includes('reconnect drive') || msg.includes('invalid_grant') || msg.includes('invalid credentials');
};

export default function DrivePanel({ projectId, projectName, isAdmin }) {
  const confirm = useConfirm();
  const [status,      setStatus]      = useState(null);   // { connected, email }
  const [tokenExpired, setTokenExpired] = useState(false);
  const [files,       setFiles]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [uploading,   setUploading]   = useState(false);
  const [folderReady, setFolderReady] = useState(false);
  const [folderId,    setFolderId]    = useState(null);
  const [folderUrl,   setFolderUrl]   = useState(null);
  const [search,      setSearch]      = useState('');
  const [view,        setView]        = useState('grid');   // grid | list
  const [newDocModal, setNewDocModal] = useState(false);
  const [newDocName,  setNewDocName]  = useState('');
  const [newDocType,  setNewDocType]  = useState('doc');
  const [shareModal,  setShareModal]  = useState(null);   // fileId | null
  const [shareEmail,  setShareEmail]  = useState('');
  const [shareRole,   setShareRole]   = useState('reader');
  const [dragging,    setDragging]    = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { done, total, name }
  const fileRef = useRef();

  const checkStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await driveAPI.getStatus();
      setStatus(r.data);
      if (r.data?.connected) setTokenExpired(false);
    } catch { setStatus({ connected: false }); }
    setLoading(false);
  }, []);

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const r = await driveAPI.listFiles(projectId, search ? { search } : {});
      setFiles(r.data.files || []);
      setFolderReady(!!r.data.folderId);
      setFolderId(r.data.folderId);
      setFolderUrl(r.data.folderUrl);
    } catch (e) {
      if (isTokenExpiredMsg(e)) { setTokenExpired(true); setStatus({ connected: false }); setFiles([]); }
      else if (e.response?.status === 404 || !e.response) setFolderReady(false);
      else toast.error('Failed to load files');
    }
    setLoading(false);
  }, [projectId, search]);

  // ── Bootstrap ─────────────────────────────────────────────────────────────
  useEffect(() => {
    checkStatus();
    // Listen for OAuth popup response
    const handler = (e) => {
      if (e.data?.type === 'DRIVE_CONNECTED') { toast.success(`Drive connected as ${e.data.email}`); checkStatus(); }
      if (e.data?.type === 'DRIVE_ERROR')     { toast.error(`Drive error: ${e.data.message}`); }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [checkStatus]);

  useEffect(() => {
    if (status?.connected && projectId) loadFiles();
  }, [status, projectId, search, loadFiles]);

  // ── Connect Google Drive (OAuth popup) ───────────────────────────────────
  const connectDrive = async () => {
    try {
      const r = await driveAPI.getAuthUrl();
      const popup = window.open(r.data.url, 'driveAuth', 'width=500,height=650,left=200,top=100');
      if (!popup) toast.error('Please allow popups for this site');
    } catch { toast.error('Failed to get auth URL'); }
  };

  const disconnect = async () => {
    const ok = await confirm('Disconnect Google Drive? Files in Drive won\'t be deleted.', { title: 'Disconnect Drive?', variant: 'warning' });
    if (!ok) return;
    try { await driveAPI.disconnect(); setStatus({ connected: false }); setFiles([]); toast.success('Disconnected'); }
    catch { toast.error('Failed to disconnect'); }
  };

  // ── Create folder for project ────────────────────────────────────────────
  const createFolder = useCallback(async () => {
    try {
      const r = await driveAPI.createFolder(projectId);
      setFolderReady(true); setFolderId(r.data.folder.id); setFolderUrl(r.data.folder.webViewLink);
      toast.success(r.data.existing ? 'Folder already exists' : `Folder "${r.data.folder.name}" created in Drive!`);
      loadFiles();
    } catch (e) {
      if (isTokenExpiredMsg(e)) { setTokenExpired(true); setStatus({ connected: false }); }
      else toast.error(e.response?.data?.message || 'Failed to create folder');
    }
  }, [projectId, loadFiles]);

  // ── Upload files (input or drag-drop) ────────────────────────────────────
  // Sends multipart/form-data to our backend which streams to Google Drive.
  // Avoids CORS issues that arise when uploading direct-to-Google from a browser.
  const handleFiles = useCallback(async (fileList) => {
    if (!fileList.length) return;
    if (!folderReady) { await createFolder(); }
    setUploading(true);
    const toUpload = Array.from(fileList);
    let done = 0;

    for (let i = 0; i < toUpload.length; i++) {
      const file = toUpload[i];
      setUploadProgress({ done: i, total: toUpload.length, name: file.name, bytes: 0, bytesTotal: file.size });
      try {
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              setUploadProgress(prev => prev ? { ...prev, bytes: e.loaded, bytesTotal: e.total } : prev);
            }
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else {
              try { reject(new Error(JSON.parse(xhr.responseText).message || `Upload failed: ${xhr.status}`)); }
              catch { reject(new Error(`Upload failed: ${xhr.status}`)); }
            }
          };
          xhr.onerror = () => reject(new Error('Network error during upload'));
          xhr.onabort = () => reject(new Error('Upload aborted'));

          const formData = new FormData();
          formData.append('file', file);

          const token = localStorage.getItem('token');
          const base = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/+$/, '');
          xhr.open('POST', `${base}/drive/projects/${projectId}/upload-multipart`);
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.send(formData);
        });

        done++;
      } catch (err) {
        toast.error(`Failed to upload ${file.name}: ${err.message}`);
      }
    }

    setUploadProgress(null);
    setUploading(false);
    if (done) { toast.success(`${done} file${done > 1 ? 's' : ''} uploaded to Drive`); loadFiles(); }
  }, [projectId, folderReady, createFolder]);

  // ── Create Google Doc/Sheet/Slide ────────────────────────────────────────
  const createDoc = async () => {
    if (!newDocName.trim()) return toast.error('Enter a name');
    try {
      const r = await driveAPI.createDoc(projectId, { name: newDocName, type: newDocType });
      toast.success(`${newDocType} created!`);
      setNewDocModal(false); setNewDocName(''); loadFiles();
      window.open(r.data.file.webViewLink, '_blank');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  // ── Share file ────────────────────────────────────────────────────────────
  const shareFile = async () => {
    if (!shareEmail.trim()) return toast.error('Enter an email');
    try {
      await driveAPI.shareFile(shareModal, { email: shareEmail, role: shareRole });
      toast.success(`Shared with ${shareEmail}`);
      setShareModal(null); setShareEmail('');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to share'); }
  };

  // ── Delete file ───────────────────────────────────────────────────────────
  const deleteFile = async (file) => {
    const ok = await confirm(`Delete "${file.name}" from Drive?`, { title: 'Delete File?', variant: 'danger' });
    if (!ok) return;
    try { await driveAPI.deleteFile(file.id); toast.success('Deleted'); loadFiles(); }
    catch { toast.error('Failed to delete'); }
  };

  // ── Drag-drop handlers ───────────────────────────────────────────────────
  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const S = {
    card:    { background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 16px #0000000a', border:'1px solid #f1f5f9' },
    btn:     (v) => ({ padding:'9px 18px', borderRadius:10, border:'none', fontWeight:600, fontSize:13, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6, transition:'all .2s',
      ...(v==='primary'   ? { background:'linear-gradient(135deg,#4285f4,#1a73e8)', color:'#fff' }
        : v==='danger'    ? { background:'#fee2e2', color:'#dc2626' }
        : v==='secondary' ? { background:'#f8fafc', color:'#475569', border:'1.5px solid #e2e8f0' }
        :                   { background:'#f1f5f9', color:'#64748b' }) }),
    inp:     { width:'100%', padding:'9px 14px', border:'2px solid #e2e8f0', borderRadius:10, fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:"'Sora',sans-serif" },
    overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 },
    modal:   { background:'#fff', borderRadius:20, padding:32, width:440, maxWidth:'92vw', boxShadow:'0 24px 80px #0000002a' },
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading && !status) {
    return (
      <div style={{ ...S.card, textAlign:'center', padding:48 }}>
        <div style={{ width:36, height:36, border:'3px solid #f1f5f9', borderTop:'3px solid #4285f4', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 12px' }}/>
        <div style={{ color:'#94a3b8', fontSize:13 }}>Checking Drive connection...</div>
      </div>
    );
  }

  // ── Not configured (no credentials on server) ────────────────────────────
  if (status?.configured === false) {
    return (
      <div style={{ ...S.card, textAlign:'center', padding:'40px 32px' }}>
        <div style={{ fontSize:40, marginBottom:12 }}>🔧</div>
        <h3 style={{ margin:'0 0 8px', fontSize:18, fontWeight:700, color:'#0f172a', fontFamily:"'Sora',sans-serif" }}>Google Drive Not Configured</h3>
        <p style={{ color:'#64748b', fontSize:13, margin:0, lineHeight:1.6, maxWidth:360, marginLeft:'auto', marginRight:'auto' }}>
          Google OAuth credentials are not set on this server.<br/>
          Add <code style={{ background:'#f1f5f9', padding:'1px 6px', borderRadius:4 }}>GOOGLE_CLIENT_ID</code> and <code style={{ background:'#f1f5f9', padding:'1px 6px', borderRadius:4 }}>GOOGLE_CLIENT_SECRET</code> to the backend <code style={{ background:'#f1f5f9', padding:'1px 6px', borderRadius:4 }}>.env</code> file to enable Drive integration.
        </p>
      </div>
    );
  }

  // ── Not connected (or token expired) ─────────────────────────────────────
  if (!status?.connected) {
    return (
      <div style={{ ...S.card, textAlign:'center', padding:'48px 32px' }}>
        {tokenExpired && (
          <div style={{ background:'#fff7ed', border:'1.5px solid #fed7aa', borderRadius:12, padding:'12px 20px', marginBottom:24, fontSize:13, color:'#92400e', display:'inline-flex', alignItems:'center', gap:8 }}>
            ⚠️ <strong>Drive token expired.</strong> Please reconnect to restore access.
          </div>
        )}
        <div style={{ fontSize:56, marginBottom:16 }}>
          <svg width="56" height="56" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
            <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
            <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47"/>
            <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/>
            <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
            <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
            <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
          </svg>
        </div>
        <h2 style={{ margin:'0 0 8px', fontSize:22, fontWeight:800, color:'#0f172a', fontFamily:"'Sora',sans-serif" }}>
          {tokenExpired ? 'Reconnect Google Drive' : 'Connect Google Drive'}
        </h2>
        <p style={{ color:'#64748b', fontSize:14, margin:'0 0 28px', lineHeight:1.6 }}>
          {tokenExpired
            ? 'Your Google authorization has expired. Click below to reconnect and restore Drive access.'
            : 'Link your Google Drive to upload, manage and share project files directly from here.'}
        </p>
        {isAdmin
          ? <button style={S.btn('primary')} onClick={connectDrive}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/></svg>
              {tokenExpired ? 'Reconnect Google Drive' : 'Connect Google Drive'}
            </button>
          : <div style={{ background:'#f1f5f9', borderRadius:10, padding:'12px 20px', fontSize:13, color:'#64748b' }}>
              Ask your admin to reconnect Google Drive in Settings.
            </div>
        }
        <div style={{ marginTop:20, fontSize:12, color:'#94a3b8' }}>
          Files are stored in your own Google Drive account under a folder named <strong>[PM] {projectName}</strong>
        </div>
      </div>
    );
  }

  // ── Connected — no folder yet ─────────────────────────────────────────────
  if (!folderReady && !loading) {
    return (
      <div style={S.card}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <svg width="22" height="22" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg"><path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/><path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47"/><path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/><path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/><path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/><path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/></svg>
            <div>
              <div style={{ fontWeight:700, fontSize:15, color:'#0f172a' }}>Google Drive</div>
              <div style={{ fontSize:11, color:'#10b981' }}>✓ Connected as {status.email}</div>
            </div>
          </div>
          {isAdmin && <button style={S.btn('danger')} onClick={disconnect}>Disconnect</button>}
        </div>
        <div style={{ background:'#f8fafc', borderRadius:14, padding:36, textAlign:'center', border:'2px dashed #e2e8f0' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>📁</div>
          <h3 style={{ margin:'0 0 8px', color:'#0f172a', fontFamily:"'Sora',sans-serif" }}>No Drive folder yet</h3>
          <p style={{ color:'#94a3b8', fontSize:13, margin:'0 0 20px' }}>Create a dedicated folder in your Drive for <strong>{projectName}</strong></p>
          <button style={S.btn('primary')} onClick={createFolder}>
            📁 Create Project Folder in Drive
          </button>
        </div>
      </div>
    );
  }

  // ── Main file browser ─────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes spin { to { transform:rotate(360deg) } }
        @keyframes driveSlide { 0%{left:-40%} 100%{left:100%} }
        .df-row:hover { background:#f8fafc; }
        .df-card:hover { border-color:#4285f4!important; box-shadow:0 4px 20px #4285f41a; }
        .df-action { opacity:0; transition:opacity .15s; }
        .df-card:hover .df-action, .df-row:hover .df-action { opacity:1; }
      `}</style>

      <div style={S.card}>
        {/* Header bar */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:12 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <svg width="22" height="22" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg"><path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/><path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47"/><path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/><path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/><path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/><path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/></svg>
            <div>
              <div style={{ fontWeight:700, fontSize:15, color:'#0f172a' }}>Project Files</div>
              <div style={{ fontSize:11, color:'#10b981' }}>✓ {status.email} · {files.length} file{files.length !== 1 ? 's' : ''}</div>
            </div>
          </div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {folderUrl && (
              <a href={folderUrl} target="_blank" rel="noreferrer" style={{ ...S.btn('secondary'), textDecoration:'none' }}>
                🔗 Open in Drive
              </a>
            )}
            <button style={S.btn('secondary')} onClick={() => setNewDocModal(true)}>✏️ New Doc</button>
            <button style={S.btn('primary')} onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploadProgress
                ? `⏳ ${uploadProgress.done}/${uploadProgress.total} files...`
                : uploading ? '⏳ Uploading...' : '⬆️ Upload'}
            </button>
            <input ref={fileRef} type="file" multiple style={{ display:'none' }} onChange={e => handleFiles(e.target.files)}/>
            <button style={{ ...S.btn(), fontSize:18, padding:'6px 10px' }} onClick={() => setView(v => v === 'grid' ? 'list' : 'grid')}>
              {view === 'grid' ? '☰' : '⊞'}
            </button>
            {isAdmin && <button style={S.btn('danger')} onClick={disconnect}>Disconnect</button>}
          </div>
        </div>

        {/* Search */}
        <div style={{ position:'relative', marginBottom:18 }}>
          <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#94a3b8', fontSize:15 }}>🔍</span>
          <input style={{ ...S.inp, paddingLeft:36 }} placeholder="Search files in this project..." value={search}
            onChange={e => setSearch(e.target.value)}/>
        </div>

        {/* Drag-drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          style={{ border:`2px dashed ${dragging ? '#4285f4' : '#e2e8f0'}`, borderRadius:12, padding: files.length ? '12px 16px' : '40px 16px',
            background: dragging ? '#eff6ff' : 'transparent', transition:'all .2s', textAlign: files.length ? 'left' : 'center', marginBottom:16 }}>
          {files.length === 0 && !loading && (
            <div>
              <div style={{ fontSize:40, marginBottom:8 }}>☁️</div>
              <div style={{ fontWeight:600, color:'#475569', fontSize:14 }}>Drop files here to upload</div>
              <div style={{ color:'#94a3b8', fontSize:12, marginTop:4 }}>or use the Upload button above</div>
            </div>
          )}
          {files.length > 0 && <div style={{ fontSize:12, color:'#94a3b8' }}>📂 Drop more files to upload to this project's folder</div>}
        </div>

        {/* Upload progress bar */}
        {uploadProgress && (() => {
          const hasBytes  = uploadProgress.bytes > 0 && uploadProgress.bytesTotal > 0;
          const bytesPct  = hasBytes ? Math.round((uploadProgress.bytes / uploadProgress.bytesTotal) * 100) : 0;
          const indeterminate = !hasBytes; // show animated sweep when bytes unknown
          const fmtMB     = (b) => b >= 1048576 ? `${(b/1048576).toFixed(1)} MB` : `${(b/1024).toFixed(0)} KB`;
          return (
            <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:10, padding:'12px 16px', marginBottom:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#1e40af', minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  ⬆️ {uploadProgress.name}
                </div>
                <div style={{ fontSize:12, color:'#3b82f6', fontWeight:700, flexShrink:0, marginLeft:8 }}>
                  {uploadProgress.total > 1 ? `${uploadProgress.done + 1}/${uploadProgress.total} files` : ''}
                  {!indeterminate && (uploadProgress.total > 1 ? ` · ${bytesPct}%` : `${bytesPct}%`)}
                </div>
              </div>
              <div style={{ background:'#dbeafe', borderRadius:6, height:7, overflow:'hidden', position:'relative' }}>
                {indeterminate ? (
                  <div style={{
                    position:'absolute', height:'100%', width:'40%', borderRadius:6,
                    background:'linear-gradient(90deg,#3b82f6,#1d4ed8)',
                    animation:'driveSlide 1.4s ease-in-out infinite',
                  }}/>
                ) : (
                  <div style={{
                    height:'100%', borderRadius:6,
                    background:'linear-gradient(90deg,#3b82f6,#1d4ed8)',
                    width: `${bytesPct}%`,
                    transition:'width .2s ease',
                  }}/>
                )}
              </div>
              <div style={{ fontSize:11, color:'#3b82f6', marginTop:5 }}>
                {indeterminate
                  ? 'Uploading to Google Drive…'
                  : `${fmtMB(uploadProgress.bytes)} / ${fmtMB(uploadProgress.bytesTotal)}`}
              </div>
            </div>
          );
        })()}

        {/* Loading spinner */}
        {loading && (
          <div style={{ textAlign:'center', padding:32 }}>
            <div style={{ width:32, height:32, border:'3px solid #f1f5f9', borderTop:'3px solid #4285f4', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto' }}/>
          </div>
        )}

        {/* ── GRID VIEW ──────────────────────────────────────────────────── */}
        {!loading && view === 'grid' && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))', gap:14 }}>
            {files.map(f => {
              const meta = fileMeta(f.mimeType);
              return (
                <div key={f.id} className="df-card"
                  style={{ background:'#fafbfc', borderRadius:14, padding:16, border:'2px solid #f1f5f9', cursor:'pointer', transition:'all .2s', position:'relative' }}
                  onClick={() => window.open(f.webViewLink, '_blank')}>
                  <div style={{ fontSize:36, marginBottom:10 }}>{meta.icon}</div>
                  <div style={{ fontSize:12, fontWeight:700, color:'#0f172a', marginBottom:4, wordBreak:'break-word', lineHeight:1.3 }}>{f.name}</div>
                  <div style={{ fontSize:10, color:'#94a3b8', marginBottom:8 }}>
                    <span style={{ background: meta.color + '22', color: meta.color, padding:'2px 7px', borderRadius:6, fontWeight:700 }}>{meta.label}</span>
                  </div>
                  <div style={{ fontSize:10, color:'#94a3b8' }}>{fmtSize(parseInt(f.size))}</div>
                  <div style={{ fontSize:10, color:'#94a3b8' }}>{fmtDate(f.modifiedTime)}</div>
                  {/* Actions */}
                  <div className="df-action" style={{ position:'absolute', top:8, right:8, display:'flex', gap:4 }}
                    onClick={e => e.stopPropagation()}>
                    <button title="Share" onClick={() => { setShareModal(f.id); setShareEmail(''); }}
                      style={{ width:26, height:26, borderRadius:6, border:'none', background:'#fff', color:'#4285f4', cursor:'pointer', fontSize:13, boxShadow:'0 1px 4px #0002' }}>↗</button>
                    {isAdmin && <button title="Delete" onClick={() => deleteFile(f)}
                      style={{ width:26, height:26, borderRadius:6, border:'none', background:'#fff', color:'#ef4444', cursor:'pointer', fontSize:13, boxShadow:'0 1px 4px #0002' }}>🗑</button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── LIST VIEW ──────────────────────────────────────────────────── */}
        {!loading && view === 'list' && (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:'#f8fafc' }}>
                {['File','Type','Size','Modified',''].map(h => (
                  <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', borderBottom:'2px solid #f1f5f9' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {files.map(f => {
                const meta = fileMeta(f.mimeType);
                return (
                  <tr key={f.id} className="df-row" style={{ cursor:'pointer', transition:'background .15s' }} onClick={() => window.open(f.webViewLink, '_blank')}>
                    <td style={{ padding:'11px 12px', fontWeight:600, color:'#0f172a', display:'flex', alignItems:'center', gap:10 }}>
                      <span style={{ fontSize:20 }}>{meta.icon}</span>
                      <span style={{ maxWidth:240, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name}</span>
                    </td>
                    <td style={{ padding:'11px 12px' }}>
                      <span style={{ background: meta.color + '22', color: meta.color, padding:'2px 8px', borderRadius:6, fontSize:11, fontWeight:700 }}>{meta.label}</span>
                    </td>
                    <td style={{ padding:'11px 12px', color:'#64748b' }}>{fmtSize(parseInt(f.size))}</td>
                    <td style={{ padding:'11px 12px', color:'#64748b' }}>{fmtDate(f.modifiedTime)}</td>
                    <td style={{ padding:'11px 12px' }} onClick={e => e.stopPropagation()}>
                      <div className="df-action" style={{ display:'flex', gap:6 }}>
                        {f.webContentLink && (
                          <a href={f.webContentLink} download onClick={e => e.stopPropagation()}
                            style={{ padding:'4px 10px', borderRadius:7, background:'#f1f5f9', color:'#475569', textDecoration:'none', fontSize:12, fontWeight:600 }}>⬇ Download</a>
                        )}
                        <button onClick={() => { setShareModal(f.id); setShareEmail(''); }}
                          style={{ padding:'4px 10px', borderRadius:7, border:'none', background:'#eff6ff', color:'#4285f4', cursor:'pointer', fontSize:12, fontWeight:600 }}>Share</button>
                        {isAdmin && (
                          <button onClick={() => deleteFile(f)}
                            style={{ padding:'4px 10px', borderRadius:7, border:'none', background:'#fee2e2', color:'#dc2626', cursor:'pointer', fontSize:12, fontWeight:600 }}>Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {!loading && files.length === 0 && folderReady && (
          <div style={{ textAlign:'center', padding:'32px 16px', color:'#94a3b8' }}>
            <div style={{ fontSize:40, marginBottom:10 }}>📭</div>
            <div style={{ fontWeight:600, fontSize:14 }}>{search ? 'No files match your search' : 'No files yet'}</div>
            <div style={{ fontSize:12, marginTop:4 }}>{search ? 'Try a different search term' : 'Upload files or create a Google Doc to get started'}</div>
          </div>
        )}
      </div>

      {/* ── New Doc Modal ─────────────────────────────────────────────────── */}
      {newDocModal && (
        <div style={S.overlay} onClick={() => setNewDocModal(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin:'0 0 20px', fontSize:18, fontWeight:800, color:'#0f172a', fontFamily:"'Sora',sans-serif" }}>Create Google File</h3>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:20 }}>
              {[['doc','📄','Document','#4285f4'],['sheet','📊','Spreadsheet','#34a853'],['slide','📽','Presentation','#fbbc04']].map(([type,icon,label,color]) => (
                <button key={type} onClick={() => setNewDocType(type)}
                  style={{ padding:'16px 8px', borderRadius:12, border:`2px solid ${newDocType===type ? color : '#e2e8f0'}`, background: newDocType===type ? color+'18' : '#fafbfc',
                    cursor:'pointer', textAlign:'center', transition:'all .2s' }}>
                  <div style={{ fontSize:28, marginBottom:6 }}>{icon}</div>
                  <div style={{ fontSize:12, fontWeight:700, color: newDocType===type ? color : '#64748b' }}>{label}</div>
                </button>
              ))}
            </div>
            <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:6 }}>File Name</label>
            <input style={{ ...S.inp, marginBottom:20 }} placeholder={`${projectName} - ${newDocType}`}
              value={newDocName} onChange={e => setNewDocName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createDoc()}/>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button style={S.btn('secondary')} onClick={() => setNewDocModal(false)}>Cancel</button>
              <button style={S.btn('primary')} onClick={createDoc}>Create & Open</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Share Modal ───────────────────────────────────────────────────── */}
      {shareModal && (
        <div style={S.overlay} onClick={() => setShareModal(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin:'0 0 20px', fontSize:18, fontWeight:800, color:'#0f172a', fontFamily:"'Sora',sans-serif" }}>Share File</h3>
            <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:6 }}>Email Address</label>
            <input style={{ ...S.inp, marginBottom:14 }} type="email" placeholder="colleague@company.com"
              value={shareEmail} onChange={e => setShareEmail(e.target.value)}/>
            <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:6 }}>Permission</label>
            <div style={{ display:'flex', gap:8, marginBottom:20 }}>
              {[['reader','👁 Viewer'],['commenter','💬 Commenter'],['writer','✏️ Editor']].map(([r,l]) => (
                <button key={r} onClick={() => setShareRole(r)}
                  style={{ flex:1, padding:'9px 6px', borderRadius:9, border:`2px solid ${shareRole===r ? '#4285f4' : '#e2e8f0'}`,
                    background: shareRole===r ? '#eff6ff' : '#fafbfc', color: shareRole===r ? '#4285f4' : '#64748b', cursor:'pointer', fontSize:12, fontWeight:700, transition:'all .2s' }}>
                  {l}
                </button>
              ))}
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button style={S.btn('secondary')} onClick={() => setShareModal(null)}>Cancel</button>
              <button style={S.btn('primary')} onClick={shareFile}>Share</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
